import type { CustomerOverview } from '@vy/api-definition';
import type { CustomFieldValue } from '@vy/custom-fields-types';
import { CUSTOMERS_COLUMNS_KEYS } from '@vy/customers-types';
import { DEFAULT_CURRENCY } from '@vy/types';
import { isEmptyArray, isNotNullOrUndefined } from '@vy/utils';

import { formatDate } from '../../../../utils/date';
import { extractUniqueCustomFieldNames } from '../../../../utils/extract-unique-custom-field-names';
import { escapeCsvValue } from '@vy/string';
import { CUSTOMERS_PAGE_CSV_HEADERS } from './customers-page-csv-headers';
import { getCustomerTaxTypeLabel } from './get-customer-tax-type-label';

type CSVFieldOrder = {
  field: string;
  header: string;
  getValue: (customer: CustomerOverview) => string;
};

const CSV_FIELD_ORDER: CSVFieldOrder[] = [
  {
    field: CUSTOMERS_COLUMNS_KEYS.STATUS,
    header: CUSTOMERS_PAGE_CSV_HEADERS.STATUS,
    getValue: (c) => c.status,
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.NAME,
    header: CUSTOMERS_PAGE_CSV_HEADERS.NAME,
    getValue: (c) => c.name,
  },
  {
    field: 'currency',
    header: CUSTOMERS_PAGE_CSV_HEADERS.CURRENCY,
    getValue: (c) => c.currency ?? DEFAULT_CURRENCY,
  },
  {
    field: 'externalId',
    header: CUSTOMERS_PAGE_CSV_HEADERS.EXTERNAL_ID,
    getValue: (c) => c.externalId ?? '-',
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.ACCRUED,
    header: CUSTOMERS_PAGE_CSV_HEADERS.CONTRACTS_COUNT,
    getValue: (c) => String(c.contractsCount),
  },
  {
    field: 'activeContractsCount',
    header: CUSTOMERS_PAGE_CSV_HEADERS.ACTIVE_CONTRACTS_COUNT,
    getValue: (c) => String(c.activeContractsCount),
  },
  { field: 'billingInterval', header: CUSTOMERS_PAGE_CSV_HEADERS.BILLING_INTERVAL, getValue: () => '-' },
  { field: 'planDuration', header: CUSTOMERS_PAGE_CSV_HEADERS.PLAN_DURATION, getValue: () => '-' },
  {
    field: CUSTOMERS_COLUMNS_KEYS.PARENT_CUSTOMER,
    header: CUSTOMERS_PAGE_CSV_HEADERS.PARENT_CUSTOMER,
    getValue: (c) => c.parentCustomer ?? '-',
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.CREATED_AT,
    header: CUSTOMERS_PAGE_CSV_HEADERS.CREATED_AT,
    getValue: (c) => formatDate(c.createdAt, 'MM-DD-YYYY'),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.UPDATED_AT,
    header: 'Updated At',
    getValue: (c) => formatDate(c.updatedAt, 'MM-DD-YYYY'),
  },
  {
    field: 'isChildCustomer',
    header: CUSTOMERS_PAGE_CSV_HEADERS.IS_CHILD_CUSTOMER,
    getValue: (c) => String(c.isChildCustomer),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.SUBSIDIARY,
    header: CUSTOMERS_PAGE_CSV_HEADERS.SUBSIDIARY,
    getValue: (c) => c.subsidiary ?? '-',
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.ADDRESS,
    header: CUSTOMERS_PAGE_CSV_HEADERS.ADDRESS,
    getValue: (c) => c.address ?? '-',
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.COUNTRY,
    header: CUSTOMERS_PAGE_CSV_HEADERS.COUNTRY,
    getValue: (c) => c.country ?? '-',
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.STATE,
    header: CUSTOMERS_PAGE_CSV_HEADERS.STATE,
    getValue: (c) => c.state ?? '-',
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.CITY,
    header: CUSTOMERS_PAGE_CSV_HEADERS.CITY,
    getValue: (c) => c.city ?? '-',
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.POSTAL_CODE,
    header: CUSTOMERS_PAGE_CSV_HEADERS.POSTAL_CODE,
    getValue: (c) => c.postalCode ?? '-',
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.PAYMENT_DUE_DAYS,
    header: CUSTOMERS_PAGE_CSV_HEADERS.PAYMENT_DUE_DAYS,
    getValue: (c) => String(c.paymentDueDays ?? '-'),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.MANUAL_REVIEW,
    header: CUSTOMERS_PAGE_CSV_HEADERS.MANUAL_REVIEW,
    getValue: (c) => (isNotNullOrUndefined(c.manualReview) ? (c.manualReview ? 'On' : 'Off') : '-'),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.TAX_ID,
    header: CUSTOMERS_PAGE_CSV_HEADERS.TAX_ID,
    getValue: (c) => (c.taxIds ?? []).filter(Boolean).join(', ') || '-',
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.TAX_TYPE,
    header: CUSTOMERS_PAGE_CSV_HEADERS.TAX_TYPE,
    getValue: (c) => getCustomerTaxTypeLabel(c.taxRate),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.WALLET,
    header: CUSTOMERS_PAGE_CSV_HEADERS.WALLET,
    getValue: (c) => c.wallet ?? '-',
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.ALIASES,
    header: CUSTOMERS_PAGE_CSV_HEADERS.ALIASES,
    getValue: (c) => (c.aliases ?? []).filter(Boolean).join(', ') || '-',
  },
];

export function generateCSV(customers: CustomerOverview[], visibleFields?: string[]) {
  const customFieldNames = extractUniqueCustomFieldNames(
    customers.map((customer) => customer.customFieldValues),
  );
  const fieldOrder = [...CSV_FIELD_ORDER, ...buildCustomFieldOrder(customFieldNames)];
  const fieldsToExport = getFieldsToExport(visibleFields, fieldOrder);
  const headers = generateCSVHeaders(fieldsToExport, fieldOrder);
  const rows = generateCSVRows(customers, fieldsToExport, fieldOrder);

  return `${headers.join(',')}\n${rows.join('\n')}`;
}

function getFieldsToExport(visibleFields: string[] | undefined, fieldOrder: CSVFieldOrder[]): string[] {
  if (!isNotNullOrUndefined(visibleFields)) {
    return fieldOrder.map((item) => item.field);
  }

  const fields = fieldOrder.filter((item) => visibleFields.includes(item.field)).map((item) => item.field);

  if (isEmptyArray(fields)) {
    return fieldOrder.map((item) => item.field);
  }

  return fields;
}

function generateCSVHeaders(fieldsToExport: string[], fieldOrder: CSVFieldOrder[]): string[] {
  return fieldOrder.filter((item) => fieldsToExport.includes(item.field)).map((item) => item.header);
}

function generateCSVRows(
  customers: CustomerOverview[],
  fieldsToExport: string[],
  fieldOrder: CSVFieldOrder[],
): string[] {
  const fieldsToExportSet = new Set(fieldsToExport);

  return customers.map((customer) => {
    const values = fieldOrder
      .filter((field) => fieldsToExportSet.has(field.field))
      .map((field) => escapeCsvValue(field.getValue(customer)));

    return values.join(',');
  });
}

function buildCustomFieldOrder(fieldNames: string[]): CSVFieldOrder[] {
  return fieldNames.map((fieldName) => ({
    field: fieldName,
    header: fieldName,
    getValue: (customer) => getCustomFieldValue(customer.customFieldValues, fieldName),
  }));
}

function getCustomFieldValue(customFieldValues: CustomFieldValue[] | null | undefined, fieldName: string) {
  if (!customFieldValues) {
    return '-';
  }

  const customField = customFieldValues.find((field) => field.fieldName === fieldName);
  if (!customField || customField.value === null || customField.value === undefined) {
    return '-';
  }

  return String(customField.value);
}
